import React from 'react';
import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import { FiArrowLeft, FiArrowRight, FiCheck, FiLock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPaidAccess } from '../entitlements';
import { PRO_PRICE_LABEL, upgradeMessage } from '../upgradeExperience';
import ProCheckoutButton from './ProCheckoutButton';
import PlanComparison from './PlanComparison';
import FeatureValuePreview from './FeatureValuePreview';

function UpgradeGate({
  user,
  context = 'default',
  title,
  description,
  evidence = [],
  children,
  onBack,
  backLabel,
  onCheckout,
}) {
  const { entitlements } = useEntitlements();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const message = upgradeMessage(context, language);
  const visibleEvidence = evidence.filter((item) => item && item.value !== undefined && item.value !== null);
  const outcomes = Array.isArray(message.outcomes) ? message.outcomes : [];
  const previewType = {
    interview: 'interview',
    learning_pathways: 'learning',
    company_radar: 'radar',
  }[context];

  if (hasPaidAccess(entitlements)) return children || null;

  const gate = (
    <section className={`js-upgrade-gate is-${context} ${previewType ? 'has-feature-preview' : ''}`} aria-labelledby={`upgrade-gate-title-${context}`}>
      <div className="js-upgrade-gate__hero">
        <span className="js-upgrade-gate__icon" aria-hidden="true"><FiLock /></span>
        <p className="js-upgrade-gate__eyebrow">{message.eyebrow}</p>
        <h1 id={`upgrade-gate-title-${context}`}>{title || message.title}</h1>
        <p className="js-upgrade-gate__description">{description || message.description}</p>
      </div>

      {previewType && (
        <FeatureValuePreview type={previewType} showAll className="js-upgrade-gate__feature-preview" />
      )}

      {visibleEvidence.length > 0 && (
        <div className="js-upgrade-gate__evidence" aria-label={language === 'ar' ? 'نتائجك في JOBSEEQR' : 'Your JOBSEEQR results'}>
          {visibleEvidence.map((item) => (
            <div key={`${item.label}-${item.value}`}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {outcomes.length > 0 && <div className="js-upgrade-gate__outcomes">
        {outcomes.map(([outcomeTitle, outcomeBody]) => (
          <article key={outcomeTitle}>
            <FiCheck aria-hidden="true" />
            <div>
              <strong>{outcomeTitle}</strong>
              <p>{outcomeBody}</p>
            </div>
          </article>
        ))}
      </div>}

      <div className="js-upgrade-gate__decision">
        <div className="js-upgrade-gate__price" aria-label={language === 'ar' ? '20 دولاراً شهرياً' : '$20 per month'}>
          <strong>{language === 'ar' ? '20 دولاراً' : PRO_PRICE_LABEL}</strong>
          <span>{language === 'ar' ? 'شهرياً' : '/ month'}</span>
          <small>{language === 'ar' ? 'خطة واحدة · يمكنك الإلغاء في أي وقت' : 'One plan · cancel anytime'}</small>
        </div>
        <div className="js-upgrade-gate__actions">
          <ProCheckoutButton
            user={user}
            onCheckout={onCheckout}
            className="js-upgrade-gate__primary"
            busyLabel={language === 'ar' ? 'جارٍ فتح الدفع الآمن…' : 'Opening secure checkout…'}
          >
            {message.cta}
            <FiArrowRight className="js-icon--directional" aria-hidden="true" />
          </ProCheckoutButton>
          <PlanComparison user={user} onCheckout={onCheckout} className="js-upgrade-gate__secondary" />
        </div>
      </div>
    </section>
  );

  if (!onBack && context !== 'interview' && context !== 'scout_edit') return gate;

  const handleBack = onBack || (() => navigate('/career-coach'));
  const visibleBackLabel = backLabel || (language === 'ar' ? 'العودة إلى المدرب المهني' : 'Back to Career Coach');

  return (
    <div className="js-upgrade-gate-page">
      <div className="js-upgrade-gate-page__bar">
      <button type="button" className="js-secondary-back" onClick={handleBack}>
        <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
        {visibleBackLabel}
      </button>
      <PlanControlSlot /></div>
      {gate}
    </div>
  );
}

export default UpgradeGate;
